/**
 * Authentication utility functions for token management and validation
 * Supports OAuth 2.0 refresh token pattern
 */

// Storage keys
const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token'; // DEPRECATED: Refresh tokens now in httpOnly cookies
const LEGACY_TOKEN_KEY = 'token';

/**
 * Check if user has a valid auth token
 */
export function hasAuthToken(): boolean {
  const token = getAuthToken();
  return !!token;
}

/**
 * Get the current access token
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);
}

/**
 * Set the access token with validation
 * Also broadcasts to other tabs for synchronization
 */
export function setAuthToken(token: string): { success: boolean; error?: string } {
  // Enhanced validation: check format AND payload sanity
  const validation = validateTokenPayload(token);
  if (!validation.valid) {
    // Production-safe logging: Log reason but never the token itself
    console.error(`Token validation failed: ${validation.reason}`);
    import('./secure-logger')
      .then(({ authLogger }) => {
        authLogger.error(`Invalid token rejected: ${validation.reason}`);
      })
      .catch(() => {});
    return { success: false, error: validation.reason };
  }

  localStorage.setItem(ACCESS_TOKEN_KEY, token);
  // Remove legacy token if exists
  localStorage.removeItem(LEGACY_TOKEN_KEY);

  // Broadcast token update to other tabs (cross-tab sync)
  // Dynamic import to avoid circular dependencies
  import('./token-sync')
    .then(({ tokenSync }) => {
      tokenSync.broadcastTokenUpdate(token);
    })
    .catch(() => {
      // Silent fail if token-sync module not available
    });

  return { success: true };
}

/**
 * DEPRECATED: Refresh tokens are now stored in httpOnly cookies
 * This function exists for backward compatibility only
 */
export function getRefreshToken(): string | null {
  // Refresh tokens are now in httpOnly cookies - not accessible from JS
  return null;
}

/**
 * DEPRECATED: Refresh tokens are now stored in httpOnly cookies
 * Access tokens should be set via setAuthToken() directly
 */
export function setRefreshToken(_token: string): void {
  // Refresh tokens are now in httpOnly cookies - cannot be set from JS
  // This function is a no-op for backward compatibility
  return;
}

/**
 * Store access token only (refresh token is in httpOnly cookie)
 * @param accessToken - The access token to store
 * @param refreshToken - DEPRECATED: Ignored, refresh tokens are in httpOnly cookies
 */
export function setTokenPair(accessToken: string, _refreshToken?: string): void {
  setAuthToken(accessToken);
  // Refresh token is automatically managed via httpOnly cookies on the server
}

/**
 * Clear all auth tokens (logout)
 * Also broadcasts to other tabs for synchronization
 * Note: Refresh tokens in httpOnly cookies are cleared by the server
 */
export function clearAuthTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY); // Clean up any legacy refresh tokens
  localStorage.removeItem(LEGACY_TOKEN_KEY);

  // Broadcast token cleared to other tabs (cross-tab sync)
  // Dynamic import to avoid circular dependencies
  import('./token-sync')
    .then(({ tokenSync }) => {
      tokenSync.broadcastTokenCleared();
    })
    .catch(() => {
      // Silent fail if token-sync module not available
    });
}

/**
 * Validate that a token is a properly formatted JWT (3 parts separated by dots)
 * Does NOT verify signature - only checks structure
 */
export function isValidTokenFormat(token: string): boolean {
  if (!token || typeof token !== 'string') return false;

  const parts = token.split('.');
  if (parts.length !== 3) return false;

  // Verify each part is valid base64
  try {
    atob(parts[0]); // Header
    atob(parts[1]); // Payload
    // Signature (parts[2]) doesn't need to be valid base64
    return true;
  } catch {
    return false;
  }
}

/**
 * Enhanced token validation with payload sanity checks
 * Validates structure AND checks for reasonable expiration times
 */
export function validateTokenPayload(token: string): {
  valid: boolean;
  reason?: string;
} {
  // First check basic format
  if (!isValidTokenFormat(token)) {
    return { valid: false, reason: 'Invalid JWT format' };
  }

  const payload = parseJWT(token);
  if (!payload) {
    return { valid: false, reason: 'Cannot parse payload' };
  }

  // Check required fields
  if (!payload.userId) {
    return { valid: false, reason: 'Missing userId in payload' };
  }

  if (!payload.exp) {
    return { valid: false, reason: 'Missing expiration (exp) in payload' };
  }

  // Sanity check: expiration should be in the future
  const now = Math.floor(Date.now() / 1000);
  if (payload.exp < now) {
    return { valid: false, reason: 'Token already expired' };
  }

  // Sanity check: expiration shouldn't be more than 1 year in the future
  const oneYearFromNow = now + 365 * 24 * 60 * 60;
  if (payload.exp > oneYearFromNow) {
    return { valid: false, reason: 'Expiration time too far in future (>1 year)' };
  }

  // Sanity check: issued at (iat) should be in the past (allow 5 min clock skew)
  if (payload.iat && payload.iat > now + 300) {
    return { valid: false, reason: 'Token issued in the future (clock skew)' };
  }

  // Sanity check: issued at shouldn't be more than 1 year in the past
  if (payload.iat && payload.iat < now - 365 * 24 * 60 * 60) {
    return { valid: false, reason: 'Token too old (>1 year)' };
  }

  // All checks passed
  return { valid: true };
}

interface JWTPayload {
  userId?: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

/**
 * Parse JWT token payload without verification
 */
function parseJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1])) as JWTPayload;
  } catch (_error) {
    // Silent fail - parsing errors are expected for invalid tokens
    return null;
  }
}

/**
 * Get token expiration time in seconds (Unix timestamp)
 */
export function getTokenExpiration(token: string): number | null {
  const payload = parseJWT(token);
  return payload?.exp || null;
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const exp = getTokenExpiration(token);
  if (!exp) return true;

  const now = Math.floor(Date.now() / 1000);
  return exp < now;
}

/**
 * Check if access token is likely expired (basic check without parsing)
 */
export function isTokenLikelyExpired(): boolean {
  const token = getAuthToken();
  if (!token) return true;
  return isTokenExpired(token);
}

/**
 * Check if access token will expire soon (within 5 minutes)
 * Used for proactive refresh
 */
export function isTokenExpiringSoon(bufferSeconds: number = 300): boolean {
  const token = getAuthToken();
  if (!token) return false; // No token = nothing to refresh

  const exp = getTokenExpiration(token);
  if (!exp) return false; // Can't parse token = don't spam refresh

  const now = Math.floor(Date.now() / 1000);
  return exp - now < bufferSeconds;
}

/**
 * Migrate old 'token' key to 'auth_token' for consistency
 */
export function migrateAuthToken(): void {
  const oldToken = localStorage.getItem(LEGACY_TOKEN_KEY);
  const newToken = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (oldToken && !newToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, oldToken);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  }
}
